/**
 * <cart-free-shipping>
 *
 * Wraps the free-shipping progress indicator and its "complete your
 * selection" recommendation (see snippets/cart-free-shipping.liquid).
 *
 * Its only active behaviour is handling clicks on the recommendation's
 * "Add" button: it posts to the theme's existing cart/add endpoint and
 * refreshes the cart in place, using the same section-render mechanism
 * the rest of the cart already relies on (see assets/cart.js and
 * assets/cart-drawer.js) rather than introducing a second cart-update
 * system. Quantity/removal changes to the resulting line item are
 * handled entirely by the existing <cart-items> / <cart-drawer-items>
 * elements once the item is part of the cart.
 */
if (!customElements.get('cart-free-shipping')) {
  customElements.define(
    'cart-free-shipping',
    class CartFreeShipping extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        const button = event.target.closest('[data-recommendation-add]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        this.addRecommendation(button);
      }

      addRecommendation(button) {
        this.setButtonLoading(button, true);
        this.clearError();

        // <cart-drawer> (assets/cart-drawer.js) keys its section list by
        // the literal Shopify section name; <cart-items> (assets/cart.js)
        // keys it by a separate `section` field. Reuse whichever of the
        // two is actually rendering this instance, instead of keeping a
        // third copy of either list here.
        const drawer = this.closest('cart-drawer');
        const cartItems = !drawer && document.querySelector('cart-items');
        const cartItemsSections = cartItems ? cartItems.getSectionsToRender() : [];

        let sectionNames = [];
        if (drawer) {
          sectionNames = drawer.getSectionsToRender().map((section) => section.id);
        } else if (cartItems) {
          sectionNames = cartItemsSections.map((section) => section.section);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        if (sectionNames.length) {
          formData.append('sections', sectionNames);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || window.cartStrings.error);
              return;
            }

            if (drawer) {
              drawer.renderContents(response);
            } else if (cartItems) {
              this.applySections(response.sections, cartItemsSections);
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings.error);
          })
          .finally(() => {
            this.setButtonLoading(button, false);
          });
      }

      applySections(sections, sectionsToRender) {
        if (!sections) return;
        sectionsToRender.forEach((section) => {
          const target = document.getElementById(section.id);
          if (!target) return;
          const elementToReplace = (section.selector && target.querySelector(section.selector)) || target;
          elementToReplace.innerHTML = this.getSectionInnerHTML(sections[section.section], section.selector);
        });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-free-shipping' });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        if (!html) return '';
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      setButtonLoading(button, loading) {
        button.toggleAttribute('aria-disabled', loading);
        button.classList.toggle('loading', loading);
        const spinner = button.querySelector('.cart-free-shipping__recommendation-add-spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      showError(message) {
        let liveRegion = this.querySelector('.cart-free-shipping__error');
        if (!liveRegion) {
          liveRegion = document.createElement('p');
          liveRegion.className = 'cart-free-shipping__error visually-hidden';
          liveRegion.setAttribute('role', 'alert');
          this.appendChild(liveRegion);
        }
        liveRegion.textContent = message;
      }

      clearError() {
        const liveRegion = this.querySelector('.cart-free-shipping__error');
        if (liveRegion) liveRegion.textContent = '';
      }
    }
  );
}
